import CiqLogPageHeader from "components/ciq-log-page-header";
import { DatePicker, Select, Space } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import dayjs from "utils/dayjs-config";
import type { Dayjs } from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import FeatureInstanceAuditGrid, {
  type TFeatureInstanceAuditGridHandle
} from "./grids/feature-instance-audit-grid";
import ProjectCompaniesAuditGrid, {
  type TProjectCompaniesAuditGridHandle
} from "./grids/project-companies-audit-grid";
import ProjectUsersAuditGrid, {
  type TProjectUsersAuditGridHandle
} from "./grids/project-users-audit-grid";
import useEntityAuditData, {
  type TEntityAuditTabKey
} from "./hooks/entity-audit-data";
import { DATE_FORMAT_MMDDYYYY } from "utils/dateutils";

function EntityList() {
  const { RangePicker } = DatePicker;
  const gridRef = useRef<
    | TFeatureInstanceAuditGridHandle
    | TProjectUsersAuditGridHandle
    | TProjectCompaniesAuditGridHandle
    | null
  >(null);
  const [dateFilterField, setDateFilterField] = useState<
    "created_at" | "updated_at"
  >("created_at");
  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);
  const {
    organizationId,
    subscriptionId,
    projectId,
    entityTab,
    setOrganizationId,
    setSubscriptionId,
    setProjectId,
    setEntityTab,
    organizationOptions,
    subscriptionOptions,
    projectOptions,
    entityOptions,
    organizationsLoading,
    subscriptionsLoading,
    projectsLoading,
    entityList,
    entityListLoading,
    entityAuditResponse,
    projectUsers,
    projectUsersLoading,
    projectVendors,
    projectVendorsLoading
  } = useEntityAuditData();

  const filterContextKey = `${organizationId ?? ""}-${subscriptionId ?? ""}-${projectId ?? ""}-${entityTab ?? ""}`;

  useEffect(() => {
    setDateFilterField("created_at");
    setDateRange(null);
    gridRef.current?.setQuickFilterText("");
  }, [organizationId, subscriptionId, projectId, entityTab]);

  const controlsDisabled = useMemo(() => {
    if (!entityTab) {
      return true;
    }
    if (entityTab === "project_users") {
      return projectUsersLoading || projectUsers.length === 0;
    }
    if (entityTab === "project_companies") {
      return projectVendorsLoading || projectVendors.length === 0;
    }
    return entityListLoading || entityList.length === 0;
  }, [
    entityTab,
    projectUsersLoading,
    projectUsers.length,
    projectVendorsLoading,
    projectVendors.length,
    entityListLoading,
    entityList.length
  ]);

  const dateControlsDisabled = controlsDisabled;

  const featureInstanceRows = useMemo(
    () => entityAuditResponse?.featureInstances ?? [],
    [entityAuditResponse?.featureInstances]
  );

  const filteredFeatureRows = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      return featureInstanceRows;
    }

    const startDate = dateRange[0].startOf("day");
    const endDate = dateRange[1].endOf("day");

    return featureInstanceRows.filter((item) => {
      const dateValue = item?.[dateFilterField];
      if (!dateValue) return false;
      const itemDate = dayjs(dateValue);
      return itemDate.isBetween(startDate, endDate, undefined, "[]");
    });
  }, [dateFilterField, dateRange, featureInstanceRows]);

  const filteredProjectUsers = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      return projectUsers;
    }

    const startDate = dateRange[0].startOf("day");
    const endDate = dateRange[1].endOf("day");

    return projectUsers.filter((item) => {
      const dateValue =
        dateFilterField === "created_at" ? item.created_at : item.updated_at;
      if (!dateValue) return false;
      const itemDate = dayjs(dateValue);
      return itemDate.isBetween(startDate, endDate, undefined, "[]");
    });
  }, [dateFilterField, dateRange, projectUsers]);

  const filteredProjectVendors = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      return projectVendors;
    }

    const startDate = dateRange[0].startOf("day");
    const endDate = dateRange[1].endOf("day");

    return projectVendors.filter((item) => {
      const dateValue =
        dateFilterField === "created_at" ? item.created_at : item.updated_at;
      if (!dateValue) return false;
      const itemDate = dayjs(dateValue);
      return itemDate.isBetween(startDate, endDate, undefined, "[]");
    });
  }, [dateFilterField, dateRange, projectVendors]);

  return (
    <div className="flex flex-col w-full h-[calc(100vh-60px)] bg-white">
      <CiqLogPageHeader
        entityName="Audit Logs"
        titleParam={{
          title: "Audit Logs",
          totalCount: 0,
          filterStats: {
            displayCount: 0,
            filterName: undefined
          }
        }}
        items={[]}
      />
      <div className="flex flex-col flex-1 px-4 gap-4">
        <div className="flex items-end justify-between gap-3">
          <div className="flex items-center gap-3">
            <>
              <div className="flex flex-col gap-1">
                <div className="text-xs text-gray-500">Organization</div>
                <Select
                  className="w-56"
                  placeholder="Select Organization"
                  value={organizationId}
                  onChange={setOrganizationId}
                  options={organizationOptions}
                  showSearch
                  optionFilterProp="label"
                  loading={organizationsLoading}
                  allowClear
                />
              </div>
              <div className="flex flex-col gap-1">
                <div className="text-xs text-gray-500">Subscription</div>
                <Select
                  className="w-56"
                  placeholder="Select Subscription"
                  value={subscriptionId}
                  onChange={setSubscriptionId}
                  options={subscriptionOptions}
                  showSearch
                  optionFilterProp="label"
                  loading={subscriptionsLoading}
                  disabled={!organizationId}
                  allowClear
                />
              </div>
              <div className="flex flex-col gap-1">
                <div className="text-xs text-gray-500">Project</div>
                <Select
                  className="w-56"
                  placeholder="Select Project"
                  value={projectId}
                  onChange={setProjectId}
                  options={projectOptions}
                  showSearch
                  optionFilterProp="label"
                  loading={projectsLoading}
                  disabled={!subscriptionId}
                  allowClear
                />
              </div>
            </>
            <div className="flex flex-col gap-1">
              <div className="text-xs text-gray-500">Entity</div>
              <Select<TEntityAuditTabKey>
                className="w-48"
                placeholder="Select Entity"
                value={entityTab}
                onChange={(value) => {
                  setEntityTab(value);
                }}
                options={entityOptions}
                showSearch
                optionFilterProp="label"
                disabled={!projectId}
                allowClear
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Space.Compact>
              <Select
                className="w-32"
                value={dateFilterField}
                onChange={(value) => {
                  setDateFilterField(value);
                }}
                options={[
                  { label: "Created At", value: "created_at" },
                  { label: "Updated At", value: "updated_at" }
                ]}
                disabled={dateControlsDisabled}
              />
              <RangePicker
                value={dateRange}
                onChange={(value) => {
                  setDateRange(value);
                }}
                format={DATE_FORMAT_MMDDYYYY}
                disabled={dateControlsDisabled}
              />
            </Space.Compact>
            <CiqAgSearch
              key={filterContextKey}
              placeholder="Search"
              disabled={controlsDisabled}
              onSearch={(text) => {
                gridRef.current?.setQuickFilterText(text);
              }}
            />
          </div>
        </div>
        <div className="ag-theme-alpine grow min-h-0">
          {entityTab === "project_users" ? (
            <ProjectUsersAuditGrid
              ref={gridRef}
              loading={projectUsersLoading}
              filteredRows={filteredProjectUsers}
            />
          ) : entityTab === "project_companies" ? (
            <ProjectCompaniesAuditGrid
              ref={gridRef}
              loading={projectVendorsLoading}
              filteredRows={filteredProjectVendors}
            />
          ) : entityTab === "submittals" || entityTab === "materials" ? (
            <FeatureInstanceAuditGrid
              ref={gridRef}
              loading={entityListLoading}
              filteredRows={filteredFeatureRows}
            />
          ) : (
            <div className="flex h-full min-h-[200px] items-center justify-center text-sm text-gray-500">
              Select an entity type to view audit data.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EntityList;
